/**
 * I tre stadi del preavviso, lato interfaccia.
 *
 * ⚠️ **Questa logica esiste anche nel backend**
 * (`backend/src/modules/notifiche/stadi.ts`, dove c'è anche il perché di ogni
 * numero). È una duplicazione consapevole, la stessa già accettata per
 * `aggiungiMesi` in `rinnovo.ts`: chi crea un tipo di scadenza deve vedere
 * l'effetto di quello che scrive **mentre lo scrive**, e chiedere le tre date
 * al server a ogni battuta sarebbe lento e fragile per un calcolo che sta in
 * dieci righe.
 *
 * **Chi modifica una delle due deve modificare anche l'altra**, e rieseguire i
 * test di entrambe. Il caso da non sbagliare in nessuna delle due copie è il
 * collasso: se qui l'anteprima promette tre avvisi dove ne arriveranno due,
 * mente a chi si sta fidando di lei — ed è peggio che non averla.
 *
 * Il backend resta l'autorità: quello che parte davvero lo decide lui.
 *
 * ## In due righe, cosa fa
 *
 * Da un solo valore configurabile ricava fino a tre avvisi: al valore stesso,
 * a metà, e a un quarto **ma mai più di 7 giorni prima** (con un preavviso di
 * 180 giorni, un'"ultima chiamata" a 45 giorni dalla data non sarebbe una
 * ultima chiamata). Poi scarta gli avvisi troppo vicini fra loro: con un
 * preavviso di 3 giorni le percentuali darebbero tre email in tre giorni, e
 * con 2 o meno due avvisi cadrebbero lo stesso giorno.
 */

/** Distanza minima fra due avvisi consecutivi. Sotto, il secondo si scarta. */
export const DISTANZA_MINIMA_GIORNI = 3;

/** Il terzo stadio non parte mai più di così in anticipo. */
export const TETTO_ULTIMO_GIORNI = 7;

export interface Soglia {
  /** 1, 2 o 3: quale dei tre avvisi è. Non cambia se quelli prima vengono scartati. */
  stadio: 1 | 2 | 3;
  /** Giorni prima della scadenza a cui parte. */
  giorni: number;
}

/** Le soglie effettive, dalla più lontana alla più vicina e già collassate. */
export function soglieStadi(preavvisoGiorni: number): Soglia[] {
  const candidate: Soglia[] = [
    { stadio: 1, giorni: preavvisoGiorni },
    { stadio: 2, giorni: Math.ceil(preavvisoGiorni / 2) },
    { stadio: 3, giorni: Math.min(Math.ceil(preavvisoGiorni / 4), TETTO_ULTIMO_GIORNI) },
  ];

  const tenute: Soglia[] = [];
  for (const soglia of candidate) {
    const precedente = tenute[tenute.length - 1];
    if (precedente === undefined || precedente.giorni - soglia.giorni >= DISTANZA_MINIMA_GIORNI) {
      tenute.push(soglia);
    }
  }

  return tenute;
}

/**
 * «a 30, 15 e 7 giorni» — la frase da mostrare sotto al campo.
 *
 * Al singolare quando gli avvisi si riducono a uno, che con preavvisi corti
 * succede davvero: «a 2 giorni». Scriverlo al plurale in quel caso farebbe
 * sembrare l'anteprima sbagliata proprio quando è più utile.
 */
export function descriviStadi(preavvisoGiorni: number): string {
  const giorni = soglieStadi(preavvisoGiorni).map((s) => s.giorni);

  if (giorni.length === 1) {
    return giorni[0] === 0 ? 'il giorno stesso della scadenza' : `a ${giorni[0]} giorni`;
  }

  const ultimo = giorni[giorni.length - 1];
  return `a ${giorni.slice(0, -1).join(', ')} e ${ultimo} giorni`;
}
